import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, TextField, Fade, useTheme } from "@mui/material";
import { ActionModal } from "../shared";
import { lightTheme } from "../../styles/theme";
import ProfileImageSelectorModal from "../ProfileImageSelectorModal";
import ImageCropperModal from "../Advertisements/ImageCropperModal";
import { ORGANIZATION_BANNER_ASPECT_RATIO } from "../../utils/imageUpload";

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  user: any;
  token: string | null;
  onComplete: () => void;
  handleLogoFileUpload: (file: File) => Promise<boolean>;
  handleUpdateSchoolDescription: (description: string) => Promise<void>;
  handleBannerFileUpload: (base64Image: string) => Promise<boolean>;
  handleUpdatePhoneNumber: (phoneNumber: string) => Promise<void>;
}

type OnboardingStep = "logo" | "description" | "banner" | "complete";

// Check if description is a placeholder - it might be empty, or contain placeholder text
const isPlaceholderDescription = (description?: string): boolean => {
  if (!description || description.trim() === "") return true;
  // Check if it contains the common placeholder text
  const placeholderText = "We just need a little more information about your organisation to setup your Noticeboard.";
  return description === placeholderText || description.includes("setup your Noticeboard");
};

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  open,
  onClose,
  user,
  token,
  onComplete,
  handleLogoFileUpload,
  handleUpdateSchoolDescription,
  handleBannerFileUpload,
  handleUpdatePhoneNumber,
}) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("logo");
  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [description, setDescription] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [isBannerCropperOpen, setIsBannerCropperOpen] = useState(false);
  const [bannerImageToCrop, setBannerImageToCrop] = useState<string | null>(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  // Determine which step to show based on what's missing
  useEffect(() => {
    if (!open || !user) return;

    const hasLogo = user.schoolLogo && user.schoolLogo !== "" && user.schoolLogo !== "/placeholder.jpg";
    const hasDescription = user.schoolDescription && 
                          user.schoolDescription !== "" && 
                          !isPlaceholderDescription(user.schoolDescription);
    const hasBanner = user.orgBanner && user.orgBanner !== "";
    const hasPhoneNumber = user.phoneNumber && user.phoneNumber !== "";

    // Determine the first missing step
    if (!hasLogo) {
      setCurrentStep("logo");
    } else if (!hasDescription || isPlaceholderDescription(user.schoolDescription) || !hasPhoneNumber) {
      setCurrentStep("description");
    } else if (!hasBanner) {
      setCurrentStep("banner");
    } else {
      setCurrentStep("complete");
      // If everything is complete, close the modal
      setTimeout(() => {
        onComplete();
      }, 500);
    }
  }, [open, user, onComplete]);

  // Initialize description and phone number when step changes
  useEffect(() => {
    if (currentStep === "description") {
      setDescription(user?.schoolDescription || "");
      setPhoneNumber(typeof user?.phoneNumber === "string" ? user.phoneNumber : "");
    }
  }, [currentStep, user?.schoolDescription, user?.phoneNumber]);

  const handleImageSelect = useCallback(
    async (file: File) => {
      const success = await handleLogoFileUpload(file);
      if (success) {
        setUploadSuccess(true);
        setTimeout(() => {
          setUploadSuccess(false);
          setIsImageUploadModalOpen(false);
          // Move to next step - check what's still missing
          // The user object will be updated via context, so we'll re-evaluate in the useEffect
          setCurrentStep("description"); // Will be corrected by useEffect if description exists
        }, 2000);
      } else {
        setIsImageUploadModalOpen(false);
      }
    },
    [handleLogoFileUpload]
  );

  const handleImageModalClose = useCallback(() => {
    if (!uploadSuccess) {
      setIsImageUploadModalOpen(false);
    }
  }, [uploadSuccess]);

  const handleDescriptionSave = useCallback(async () => {
    // Allow saving if at least one field has content
    if (!description.trim() && !phoneNumber.trim()) {
      return;
    }
    setIsSavingDescription(true);
    try {
      // Save all fields that have content
      const savePromises: Promise<void>[] = [];
      
      if (description.trim()) {
        savePromises.push(handleUpdateSchoolDescription(description.trim()));
      }
      
      if (phoneNumber.trim()) {
        savePromises.push(handleUpdatePhoneNumber(phoneNumber.trim()));
      }
      
      await Promise.all(savePromises);
      
      setTimeout(() => {
        setIsSavingDescription(false);
        // Move to next step (banner if missing, otherwise complete)
        const hasBanner = user?.orgBanner && user.orgBanner !== "";
        if (!hasBanner) {
          setCurrentStep("banner");
        } else {
          setCurrentStep("complete");
          onComplete();
        }
      }, 500);
    } catch (error) {
      console.error("Failed to save details:", error);
      setIsSavingDescription(false);
    }
  }, [description, phoneNumber, handleUpdateSchoolDescription, handleUpdatePhoneNumber, user, onComplete]);

  const handleDescriptionSkip = useCallback(() => {
    // Move to next step (banner if missing, otherwise complete)
    const hasBanner = user?.orgBanner && user.orgBanner !== "";
    if (!hasBanner) {
      setCurrentStep("banner");
    } else {
      setCurrentStep("complete");
      onComplete();
    }
  }, [user, onComplete]);

  const handleBannerFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setBannerImageToCrop(result);
        setIsBannerCropperOpen(true);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = "";
  }, []);

  const handleBannerCropComplete = useCallback(async (croppedImageUrl: string) => {
    setIsBannerCropperOpen(false);
    setIsUploadingBanner(true);
    try {
      const success = await handleBannerFileUpload(croppedImageUrl);
      if (success) {
        setTimeout(() => {
          setIsUploadingBanner(false);
          setCurrentStep("complete");
          onComplete();
        }, 500);
      } else {
        setIsUploadingBanner(false);
      }
    } catch (error) {
      console.error("Failed to upload banner:", error);
      setIsUploadingBanner(false);
    }
  }, [handleBannerFileUpload, onComplete]);

  const handleBannerSkip = useCallback(() => {
    setCurrentStep("complete");
    onComplete();
  }, [onComplete]);

  const handleUploadImageClick = useCallback(() => {
    setIsImageUploadModalOpen(true);
  }, []);

  const getModalTitle = () => {
    switch (currentStep) {
      case "logo":
        return "Welcome to Noticeboard!";
      case "description":
        return "You're making great progress!";
      case "banner":
        return "Almost there!";
      case "complete":
        return "Thank you!";
      default:
        return "";
    }
  };

  const getModalSubtitle = () => {
    switch (currentStep) {
      case "logo":
        return "Let's personalise your school's profile.";
      case "description":
        return "Just a few final details.";
      case "banner":
        return "Add a banner image for your organization.";
      case "complete":
        return undefined;
      default:
        return "";
    }
  };

  return (
    <>
      <ActionModal
        open={open && currentStep !== "complete"}
        onClose={onClose}
        title={getModalTitle()}
        subtitle={getModalSubtitle()}
        maxWidth="md"
        disableBackdropClick={true}
      >
        <Box
          sx={{
            textAlign: "center",
            py: { xs: 2, sm: 3 },
            px: { xs: 1, sm: 2 },
            position: "relative",
            minHeight: "300px",
          }}
        >
          {/* Logo Upload Step */}
          <Fade in={currentStep === "logo"} timeout={400}>
            <Box
              sx={{
                position: currentStep === "logo" ? "relative" : "absolute",
                width: "100%",
                left: 0,
                right: 0,
                top: 0,
                pointerEvents: currentStep === "logo" ? "auto" : "none",
                opacity: currentStep === "logo" ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                  mt: 1,
                }}
              >
                <Box
                  component="img"
                  src="/icons/bookademo.webp"
                  alt="Welcome"
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "120px", sm: "150px" },
                    height: "auto",
                  }}
                />
              </Box>

              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  color: "text.secondary",
                  lineHeight: 1.7,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                Let's start by adding your school's logo. This will help personalize your Noticeboard.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexDirection: { xs: "column", sm: "row" },
                  maxWidth: "500px",
                  mx: "auto",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleUploadImageClick}
                  sx={{
                    bgcolor: "primary.main",
                    color: "common.white",
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    boxShadow: `0 2px 8px ${theme.palette.shadows.inputFocusStrong}`,
                    "&:hover": {
                      bgcolor: "primary.dark",
                      boxShadow: `0 4px 12px ${lightTheme.colors.primaryTint40}`,
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                    flex: { xs: 1, sm: "none" },
                  }}
                >
                  Upload Logo
                </Button>
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    borderColor: "divider",
                    color: "text.secondary",
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    "&:hover": {
                      borderColor: "primary.main",
                      color: "primary.main",
                      bgcolor: lightTheme.colors.primaryTint04,
                    },
                    transition: "all 0.2s ease",
                    flex: { xs: 1, sm: "none" },
                  }}
                >
                  Skip for now
                </Button>
              </Box>
            </Box>
          </Fade>

          {/* Description Step */}
          <Fade in={currentStep === "description"} timeout={400}>
            <Box
              sx={{
                position: currentStep === "description" ? "relative" : "absolute",
                width: "100%",
                left: 0,
                right: 0,
                top: 0,
                pointerEvents: currentStep === "description" ? "auto" : "none",
                opacity: currentStep === "description" ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                  mt: 1,
                }}
              >
                <Box
                  component="img"
                  src="/icons/bookademo.webp"
                  alt="Description"
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "120px", sm: "150px" },
                    height: "auto",
                  }}
                />
              </Box>

              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  color: "text.secondary",
                  lineHeight: 1.7,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                We just need a little more information about your organisation to setup your Noticeboard.
                You can update these details at any time.
              </Typography>

              <Box
                sx={{
                  maxWidth: "600px",
                  mx: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  mb: 3,
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter your school description..."
                  label="Description"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                  autoFocus
                />

                <TextField
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter your phone number..."
                  label="Phone Number"
                  type="tel"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexDirection: { xs: "column", sm: "row" },
                  maxWidth: "500px",
                  mx: "auto",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleDescriptionSave}
                  disabled={(!description.trim() && !phoneNumber.trim()) || isSavingDescription}
                  sx={{
                    bgcolor: "primary.main",
                    color: "common.white",
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    boxShadow: `0 2px 8px ${theme.palette.shadows.inputFocusStrong}`,
                    "&:hover": {
                      bgcolor: "primary.dark",
                      boxShadow: `0 4px 12px ${lightTheme.colors.primaryTint40}`,
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                    },
                    transition: "all 0.2s ease",
                    flex: { xs: 1, sm: "none" },
                  }}
                >
                  {isSavingDescription ? "Saving..." : "Save Details"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDescriptionSkip}
                  disabled={isSavingDescription}
                  sx={{
                    borderColor: "divider",
                    color: "text.secondary",
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    "&:hover": {
                      borderColor: "primary.main",
                      color: "primary.main",
                      bgcolor: lightTheme.colors.primaryTint04,
                    },
                    transition: "all 0.2s ease",
                    flex: { xs: 1, sm: "none" },
                  }}
                >
                  Skip
                </Button>
              </Box>
            </Box>
          </Fade>

          {/* Banner Upload Step */}
          <Fade in={currentStep === "banner"} timeout={400}>
            <Box
              sx={{
                position: currentStep === "banner" ? "relative" : "absolute",
                width: "100%",
                left: 0,
                right: 0,
                top: 0,
                pointerEvents: currentStep === "banner" ? "auto" : "none",
                opacity: currentStep === "banner" ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                  mt: 1,
                }}
              >
                <Box
                  component="img"
                  src="/icons/bookademo.webp"
                  alt="Banner upload"
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "120px", sm: "150px" },
                    height: "auto",
                  }}
                />
              </Box>

              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  color: "text.secondary",
                  lineHeight: 1.7,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                Add a banner image for your organization. This will be used as the background on featured cards in the mobile app.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  flexDirection: { xs: "column", sm: "row" },
                  maxWidth: "500px",
                  mx: "auto",
                }}
              >
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="banner-upload-input"
                  type="file"
                  onChange={handleBannerFileSelect}
                  disabled={isUploadingBanner}
                />
                <label htmlFor="banner-upload-input">
                  <Button
                    component="span"
                    variant="contained"
                    disabled={isUploadingBanner}
                    sx={{
                      bgcolor: "primary.main",
                      color: "common.white",
                      px: { xs: 3, sm: 4 },
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      boxShadow: `0 2px 8px ${theme.palette.shadows.inputFocusStrong}`,
                      "&:hover": {
                        bgcolor: "primary.dark",
                        boxShadow: `0 4px 12px ${lightTheme.colors.primaryTint40}`,
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                      transition: "all 0.2s ease",
                      flex: { xs: 1, sm: "none" },
                    }}
                  >
                    {isUploadingBanner ? "Uploading..." : "Upload Banner"}
                  </Button>
                </label>
                <Button
                  variant="outlined"
                  onClick={handleBannerSkip}
                  disabled={isUploadingBanner}
                  sx={{
                    borderColor: "divider",
                    color: "text.secondary",
                    px: { xs: 3, sm: 4 },
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    "&:hover": {
                      borderColor: "primary.main",
                      color: "primary.main",
                      bgcolor: lightTheme.colors.primaryTint04,
                    },
                    transition: "all 0.2s ease",
                    flex: { xs: 1, sm: "none" },
                  }}
                >
                  Skip
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>
      </ActionModal>

      {/* Image Upload Modal */}
      <ProfileImageSelectorModal
        open={isImageUploadModalOpen}
        onClose={handleImageModalClose}
        onImageSelect={handleImageSelect}
        schoolId={user?.schoolId as string}
        token={token || ""}
        showSuccess={uploadSuccess}
      />

      {/* Banner Image Cropper Modal */}
      <ImageCropperModal
        open={isBannerCropperOpen}
        imageSrc={bannerImageToCrop || ""}
        onClose={() => {
          setIsBannerCropperOpen(false);
          setBannerImageToCrop(null);
        }}
        onCropComplete={handleBannerCropComplete}
        aspectRatio={ORGANIZATION_BANNER_ASPECT_RATIO}
      />
    </>
  );
};

export default OnboardingModal;
